import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { getDueDateForApi, formatDueDate } from "../shared/utils/dueDateTime";
import GlobalTimeNavigatorBar from "../components/GlobalTimeNavigatorBar";
import { TableActionColumnHeader } from "../components/TableShell";
import OpenClosedFilterDropdown from "../components/OpenClosedFilterDropdown";
import ViewModeIconToggle from "../components/ViewModeIconToggle";
import ScheduleCalendar from "../components/ScheduleCalendar";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { validateStartEndRange } from "../shared/utils/validation";
import ConfirmationModal from "../components/ConfirmationModal";
import SurveySectionTabs, { SURVEY_TAB_MANAGEMENT } from "../components/SurveySectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import SortableCrudOptionList, {
  newSurveyOptionRow,
} from "../components/SortableCrudOptionList";
import { IconEdit, IconDuplicate, IconReopen, IconClose, IconQuestions, IconCalendar, IconView, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { intersectSelectedIdsWithRows } from "../shared/utils/entityListSelection";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");
const ErrorBanner = require("../components/ErrorBanner").default;
const { useAuth } = require("../contexts/AuthContext");

const QUESTION_TYPES = [
  { value: "single_choice", label: "Multiple Choice (Single Select)" },
  { value: "multi_choice", label: "Multiple Choice (Multi-Select)" },
  { value: "rating", label: "Rating Scale (1–5 or 1–10)" },
  { value: "likert", label: "Likert Scale (Agreement)" },
  { value: "nps", label: "NPS (0–10)" },
  { value: "open_ended", label: "Open-Ended (Short Text)" },
];

const DEFAULT_LIKERT_OPTIONS = [
  "Strongly agree",
  "Agree",
  "Neutral",
  "Disagree",
  "Strongly disagree",
];

function Surveys() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userInfo } = useAuth();
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const navViewFromUrl = (searchParams.get("navView") || "").trim().toLowerCase();
  const navDateFromUrl = (searchParams.get("navDate") || "").trim();
  const statusFromUrl = (searchParams.get("status") || "").trim().toLowerCase();

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bannerError, setBannerError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'open' | 'closed'
  const [viewMode, setViewMode] = useState("table");
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const timeNav = useGlobalTimeNavigator({
    initialView: ["day", "week", "month", "future", "all"].includes(navViewFromUrl) ? navViewFromUrl : "all",
    initialFocusDateYmd: navDateFromUrl || undefined,
  });

  useEffect(() => {
    if (statusFromUrl === "open" || statusFromUrl === "closed") setStatusFilter(statusFromUrl);
    if (["day", "week", "month", "future", "all"].includes(navViewFromUrl)) timeNav.setView(navViewFromUrl);
    if (navDateFromUrl) timeNav.setFocusDateYmd(navDateFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL-driven initial filters
  }, []);

  const [editingSurvey, setEditingSurvey] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().add(7, "day").format("YYYY-MM-DD"),
    status: "Open",
  });
  const [savingSurvey, setSavingSurvey] = useState(false);

  const [questionSurvey, setQuestionSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);

  const [closingId, setClosingId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  // Wizard state for guided create/edit flow
  const [wizardContext, setWizardContext] = useState(null); // { open, mode, step, survey, questions }
  const [wizardSummary, setWizardSummary] = useState(null); // { survey, questions }

  const loadSurveys = useCallback(() => {
    if (!account_id) return;
    setLoading(true);
    setBannerError("");
    const body = { account_id };
    if (statusFilter === "open") body.status = "Open";
    if (statusFilter === "closed") body.status = "Closed";
    apiFetch(serverUrl + "/getSurveys", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((resp) => resp.json())
      .then((data) => {
        setSurveys(Array.isArray(data) ? data : []);
      })
      .catch((err) => setSurveys([]) || setBannerError(getMessageForFetchError(err)))
      .finally(() => setLoading(false));
  }, [account_id, serverUrl, statusFilter]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const today = dayjs().startOf("day");
  const formatResponseRate = (invited, completed) => {
    if (invited == null || invited === 0) return "—";
    const pct = Math.round((Number(completed) / Number(invited)) * 100);
    return `${pct}%`;
  };
  const responseRatePct = (invited, completed) => {
    if (invited == null || invited === 0) return 0;
    return Math.round((Number(completed) / Number(invited)) * 100);
  };
  const daysElapsed = (startDate) => {
    if (!startDate) return null;
    return today.diff(dayjs(startDate).startOf("day"), "day");
  };
  const daysLeft = (endDate) => {
    if (!endDate) return null;
    return dayjs(endDate).startOf("day").diff(today, "day");
  };
  const isExpired = (s) => (s.status || "Open").toLowerCase() === "open" && s.end_date && dayjs(s.end_date).startOf("day").isBefore(today);

  function openNewSurvey() {
    setEditingSurvey({});
    setForm({
      name: "",
      description: "",
      start_date: dayjs().format("YYYY-MM-DD"),
      end_date: dayjs().add(7, "day").format("YYYY-MM-DD"),
      status: "Open",
    });
    setWizardContext({
      open: true,
      mode: "new",
      step: 1,
      surveyId: null,
      survey: {
        _id: null,
        name: "",
        description: "",
        start_date: dayjs().format("YYYY-MM-DD"),
        end_date: dayjs().add(7, "day").format("YYYY-MM-DD"),
        status: "Open",
      },
      questions: [],
    });
    setWizardSummary(null);
  }

  function openEditSurvey(s) {
    setEditingSurvey(s);
    setForm({
      name: s.name || "",
      description: s.description || "",
      start_date: s.start_date
        ? dayjs(s.start_date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      end_date: s.end_date
        ? dayjs(s.end_date).format("YYYY-MM-DD")
        : dayjs().add(7, "day").format("YYYY-MM-DD"),
      status: s.status || "Open",
    });
    setWizardContext({
      open: true,
      mode: "edit",
      step: 1,
      surveyId: s._id,
      survey: {
        _id: s._id,
        name: s.name || "",
        description: s.description || "",
        start_date: s.start_date
          ? dayjs(s.start_date).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        end_date: s.end_date
          ? dayjs(s.end_date).format("YYYY-MM-DD")
          : dayjs().add(7, "day").format("YYYY-MM-DD"),
        status: s.status || "Open",
      },
      questions: [],
    });
    setWizardSummary(null);
  }

  function handleFormChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function saveSurvey(e, options) {
    e && e.preventDefault();
    const opts = options || {};
    const skipQuestions = !!opts.skipQuestions;
    if (!account_id) {
      setConfirmDialog({ open: true, title: "Survey", message: "Missing account id", onConfirm: null, mode: "alert" });
      return;
    }
    const name = (form.name || "").trim();
    if (!name) {
      setConfirmDialog({ open: true, title: "Survey", message: "Name is required.", onConfirm: null, mode: "alert" });
      return;
    }
    const rangeCheck = validateStartEndRange(form.start_date, form.end_date, {
      granularity: "day",
      allowEqual: true,
    });
    if (!rangeCheck.valid) {
      setConfirmDialog({ open: true, title: "Survey", message: rangeCheck.message || "Start date and end date are required.", onConfirm: null, mode: "alert" });
      return;
    }
    // Send as UTC so Heroku (UTC) stores correct window: start = start of start day local, end = end of end day local
    const startDateIso = getDueDateForApi(form.start_date, "00:00");
    const endDateIso = getDueDateForApi(form.end_date, "23:59");
    if (!startDateIso || !endDateIso) {
      setConfirmDialog({ open: true, title: "Survey", message: "Start date and end date must be valid dates.", onConfirm: null, mode: "alert" });
      return;
    }
    const payload = {
      account_id,
      name,
      description: (form.description || "").trim() || undefined,
      start_date: startDateIso,
      end_date: endDateIso,
      status: form.status || "Open",
    };
    const isEdit = !!editingSurvey?._id;
    if (isEdit) {
      payload._id = editingSurvey._id;
    }
    setSavingSurvey(true);
    apiFetch(serverUrl + (isEdit ? "/updateSurvey" : "/createSurvey"), {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((resp) => {
        if (!resp.ok) {
          return resp.text().then((t) => Promise.reject(new Error(t)));
        }
        if (isEdit) {
          return resp.text();
        }
        return resp.json();
      })
      .then((data) => {
        let savedSurveyId = editingSurvey?._id;
        if (!isEdit && data && data._id) {
          savedSurveyId = data._id;
        }

        const savedSurvey =
          savedSurveyId != null
            ? {
                _id: savedSurveyId,
                name,
                description: payload.description,
                start_date: payload.start_date,
                end_date: payload.end_date,
                status: payload.status,
              }
            : null;

        setEditingSurvey(null);
        loadSurveys();

        if (skipQuestions || !savedSurvey) {
          // Wizard: user chose "Save & Close" or we cannot continue
          setWizardContext(null);
          setWizardSummary(null);
          return;
        }

        setWizardContext((prev) => {
          if (!prev) return prev;

          // Move wizard to step 2 (questions) by closing the basics modal
          // and opening the existing Questions modal for this survey.
          openQuestions(savedSurvey);

          return {
            ...prev,
            open: false,
            step: 2,
            surveyId: savedSurveyId,
            survey: savedSurvey,
          };
        });
      })
      .catch((err) =>
        setConfirmDialog({
          open: true,
          title: "Survey",
          message: err?.message || "Failed to save survey.",
          mode: "alert",
          onConfirm: null,
        })
      )
      .finally(() => setSavingSurvey(false));
  }

  function toggleSelected(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleSelectAll(visibleIds) {
    if (!visibleIds.length) return;
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  }

  function duplicateSurveyRow(s) {
    apiFetch(serverUrl + "/duplicateSurvey", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: s._id }),
    })
      .then((resp) => resp.text())
      .then((text) => {
        if (text) {
          setConfirmDialog({ open: true, title: "Survey", message: text, mode: "alert", onConfirm: null });
        }
        loadSurveys();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function deleteSelected() {
    const visibleIds = Array.from(intersectSelectedIdsWithRows(new Set(selectedIds), sorted));
    if (visibleIds.length === 0) return;
    setConfirmDialog({
      open: true,
      title: "Delete surveys",
      message: `Delete ${visibleIds.length} selected survey(s)? This will also delete their questions and responses.`,
      onConfirm: () => {
        apiFetch(serverUrl + "/deleteSurveys", {
          method: "POST",
          body: JSON.stringify({ account_id, ids: visibleIds }),
        })
          .then((resp) => resp.text())
          .then(() => {
            setSelectedIds([]);
            loadSurveys();
          })
          .catch((err) => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  function handleCloseSurveyRow(surveyId) {
    if (!account_id || !surveyId) return;
    setClosingId(surveyId);
    setBannerError("");
    apiFetch(serverUrl + "/closeSurvey", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: surveyId }),
    })
      .then((resp) => {
        if (!resp.ok) return resp.text().then((t) => Promise.reject(new Error(t)));
        return resp.text();
      })
      .then(() => loadSurveys())
      .catch((err) => setBannerError(err.message || "Failed to close survey"))
      .finally(() => setClosingId(null));
  }

  function handleReopenSurveyRow(surveyId) {
    if (!account_id || !surveyId) return;
    setReopeningId(surveyId);
    setBannerError("");
    apiFetch(serverUrl + "/reopenSurvey", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: surveyId }),
    })
      .then((resp) => {
        if (!resp.ok) return resp.text().then((t) => Promise.reject(new Error(t)));
        return resp.text();
      })
      .then(() => loadSurveys())
      .catch((err) => setBannerError(err.message || "Failed to reopen survey"))
      .finally(() => setReopeningId(null));
  }

  function buildQuestionStateFromServer(list) {
    return (list || []).map((q, idx) => {
      const qType = q.question_type || "single_choice";
      const hasOptions = Array.isArray(q.options) && q.options.length > 0;
      let optionsList = [];
      let scaleMin = q.scale_min;
      let scaleMax = q.scale_max;

      if (hasOptions) {
        optionsList = q.options.map((text) => newSurveyOptionRow(String(text ?? "")));
      } else if (qType === "likert") {
        optionsList = DEFAULT_LIKERT_OPTIONS.map((text) => newSurveyOptionRow(text));
      }

      if (qType === "rating" || qType === "nps") {
        const hasMin = scaleMin != null && !Number.isNaN(Number(scaleMin));
        const hasMax = scaleMax != null && !Number.isNaN(Number(scaleMax));
        if (!hasMin) {
          scaleMin = qType === "nps" ? 0 : 1;
        }
        if (!hasMax) {
          scaleMax = qType === "nps" ? 10 : 5;
        }
      }

      return {
        _id: q._id,
        order_index: q.order_index != null ? q.order_index : idx,
        question_text: q.question_text || "",
        question_type: qType,
        optionsList,
        scale_min: scaleMin != null ? scaleMin : "",
        scale_max: scaleMax != null ? scaleMax : "",
      };
    });
  }

  function openQuestions(s) {
    setQuestionSurvey(s);
    setQuestions([]);
    setLoadingQuestions(true);
    apiFetch(serverUrl + "/getSurveyWithQuestions", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: s._id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = Array.isArray(data.questions) ? data.questions : [];
        setQuestions(buildQuestionStateFromServer(list));
      })
      .catch((err) => {
        setQuestions([]);
        setBannerError(getMessageForFetchError(err));
      })
      .finally(() => setLoadingQuestions(false));
  }

  function closeQuestions() {
    setQuestionSurvey(null);
    setQuestions([]);
  }

  function updateQuestion(index, field, value) {
    setQuestions((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;

      const current = next[index];
      let updated = { ...current, [field]: value };

      if (field === "question_type" && value === "likert") {
        const existing = current.optionsList || [];
        if (!existing.length) {
          updated.optionsList = DEFAULT_LIKERT_OPTIONS.map((t) =>
            newSurveyOptionRow(t)
          );
        }
      }

      if (field === "question_type" && (value === "rating" || value === "nps")) {
        // Always reset rating/NPS scales to sensible defaults
        updated.scale_min = value === "nps" ? 0 : 1;
        updated.scale_max = value === "nps" ? 10 : 5;
      }

      next[index] = updated;
      return next;
    });
  }

  function addQuestion() {
    setQuestions((prev) => [
      ...prev,
      {
        _id: null,
        order_index: prev.length,
        question_text: "",
        question_type: "single_choice",
        optionsList: [],
        scale_min: "",
        scale_max: "",
      },
    ]);
  }

  function removeQuestion(index) {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  }

  function saveQuestions(e) {
    e && e.preventDefault();
    if (!questionSurvey?._id) return;

    // Require question text for every defined question row
    const missingTextIndex = questions.findIndex(
      (q) => !(q.question_text || "").trim()
    );
    if (missingTextIndex !== -1) {
      setConfirmDialog({
        open: true,
        title: "Survey questions",
        message: `Question text is required for Question ${missingTextIndex + 1}. Please enter the text or remove the question.`,
        mode: "alert",
        onConfirm: null,
      });
      return;
    }

    const payloadQuestions = questions.map((q, idx) => {
      const qt = (q.question_text || "").trim();
      const type = (q.question_type || "").trim() || "single_choice";
      const base = {
        question_text: qt,
        question_type: type,
        order_index: idx,
      };
      // Keep server question ids so existing responses stay linked after edit/save.
      if (q._id) base._id = q._id;
      if (["single_choice", "multi_choice", "likert"].includes(type)) {
        base.options = (q.optionsList || [])
          .map((row) => (row && row.value != null ? String(row.value) : "").trim())
          .filter(Boolean);
      }
      if (type === "rating" || type === "nps") {
        if (q.scale_min !== "" && !Number.isNaN(Number(q.scale_min))) {
          base.scale_min = Number(q.scale_min);
        }
        if (q.scale_max !== "" && !Number.isNaN(Number(q.scale_max))) {
          base.scale_max = Number(q.scale_max);
        }
      }
      return base;
    });
    const nonEmpty = payloadQuestions.filter(
      (q) => q.question_text && q.question_type
    );

    const performSave = () => {
      setSavingQuestions(true);
      apiFetch(serverUrl + "/saveSurveyQuestions", {
        method: "POST",
        body: JSON.stringify({
          account_id,
          survey_id: questionSurvey._id,
          questions: payloadQuestions,
        }),
      })
        .then((resp) => {
          if (resp.ok) return resp.text();
          return resp.text().then((t) => Promise.reject(new Error(t)));
        })
        .then(() => {
          if (
            wizardContext &&
            questionSurvey &&
            wizardContext.surveyId === questionSurvey._id
          ) {
            setWizardSummary({
              survey: questionSurvey,
              questions: nonEmpty,
            });
            setWizardContext((prev) => (prev ? { ...prev, step: 3 } : prev));
          }
          setConfirmDialog({
            open: true,
            title: "Survey questions",
            message: "Questions saved.",
            mode: "alert",
            onConfirm: null,
          });
          closeQuestions();
        })
        .catch((err) =>
          setConfirmDialog({
            open: true,
            title: "Survey questions",
            message: err?.message || "Failed to save questions.",
            mode: "alert",
            onConfirm: null,
          })
        )
        .finally(() => setSavingQuestions(false));
    };

    if (nonEmpty.length === 0) {
      setConfirmDialog({
        open: true,
        title: "Save without questions?",
        message:
          "You have not defined any questions. Save anyway and clear existing questions?",
        onConfirm: performSave,
      });
      return;
    }

    performSave();
  }

  const searchFiltered = useMemo(() => {
    const search = (searchTerm || "").trim().toLowerCase();
    return (surveys || []).filter((s) => {
      if (!search) return true;
      return (
        (s.name && s.name.toLowerCase().includes(search)) ||
        (s.description && s.description.toLowerCase().includes(search)) ||
        (s.status && s.status.toLowerCase().includes(search))
      );
    });
  }, [surveys, searchTerm]);

  const timeNavFiltered = useMemo(() => {
    const { bounds, view } = timeNav;
    if (view === "all") return searchFiltered;
    const rangeStart = bounds.startInclusive ? dayjs(bounds.startInclusive) : null;
    const rangeEnd = bounds.endInclusive ? dayjs(bounds.endInclusive) : null;
    return searchFiltered.filter((s) => {
      if (!s.start_date) return false;
      const start = dayjs(s.start_date);
      const end = s.end_date ? dayjs(s.end_date) : start;
      const a = start && start.isValid() ? start : end && end.isValid() ? end : null;
      const b = end && end.isValid() ? end : start && start.isValid() ? start : null;
      if (!a || !b) return false;
      if (view === "future") {
        if (rangeStart && a.isBefore(rangeStart)) return false;
        return true;
      }
      if (rangeStart && b.isBefore(rangeStart)) return false;
      if (rangeEnd && a.isAfter(rangeEnd)) return false;
      return true;
    });
  }, [searchFiltered, timeNav.view, timeNav.bounds]); // eslint-disable-line react-hooks/exhaustive-deps -- view+bounds drive range

  const sorted = useMemo(() => {
    if (!sortConfig.key) return timeNavFiltered;
    const list = [...timeNavFiltered];
    list.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "start_date" || sortConfig.key === "end_date") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (sortConfig.key === "status") {
        const statusOrder = (s) => (s.status || "").toLowerCase() === "closed" ? 0 : isExpired(s) ? 1 : 2;
        aVal = statusOrder(a);
        bVal = statusOrder(b);
      } else if (sortConfig.key === "invited" || sortConfig.key === "completed") {
        aVal = a[sortConfig.key] != null ? Number(a[sortConfig.key]) : 0;
        bVal = b[sortConfig.key] != null ? Number(b[sortConfig.key]) : 0;
      } else if (sortConfig.key === "responseRate") {
        aVal = (a.invited && Number(a.invited)) ? Number(a.completed || 0) / Number(a.invited) : 0;
        bVal = (b.invited && Number(b.invited)) ? Number(b.completed || 0) / Number(b.invited) : 0;
      } else if (sortConfig.key === "daysElapsed") {
        aVal = daysElapsed(a.start_date) ?? 0;
        bVal = daysElapsed(b.start_date) ?? 0;
      } else if (sortConfig.key === "daysLeft") {
        aVal = daysLeft(a.end_date) ?? -999;
        bVal = daysLeft(b.end_date) ?? -999;
      } else {
        aVal = (aVal || "").toString().toLowerCase();
        bVal = (bVal || "").toString().toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [timeNavFiltered, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps -- sort helpers use render-time `today`

  const visibleIds = sorted.map((s) => s._id);
  const selectedVisibleIds = intersectSelectedIdsWithRows(new Set(selectedIds), sorted);
  const selectedVisibleCount = selectedVisibleIds.size;

  function surveyCalendarLabel(s) {
    const name = (s.name || "Survey").trim() || "Survey";
    const sd = formatDueDate(s.start_date);
    const ed = s.end_date ? formatDueDate(s.end_date) : "";
    return ed && ed !== sd ? `${name} · ${sd} – ${ed}` : `${name} · ${sd}`;
  }

  function handleSort(key) {
    setSortConfig((prev) => {
      if (prev.key === key) {
        const nextDirection = prev.direction === "asc" ? "desc" : "asc";
        return { key, direction: nextDirection };
      }
      return { key, direction: "asc" };
    });
  }

  return (
    <div className="page-container page-container-full page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Create surveys, define questions, send invitations, and review responses.">
        <SurveySectionTabs activeTab={SURVEY_TAB_MANAGEMENT} />
      </SectionTabsPageHeader>
      <div className="page-content">

        <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} />

      <ConfirmationModal
        open={confirmDialog.open}
        title={confirmDialog.title || "Confirm"}
        message={confirmDialog.message}
        mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
        confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
        onConfirm={() => {
          const fn = confirmDialog.onConfirm;
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
          if (fn) fn();
        }}
        onCancel={() =>
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
        }
      />

        <div className="toolbar">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, description, or status…"
            className="input input-min-200 input-max-400"
          />
          <button type="button" onClick={() => { setSearchTerm(""); setStatusFilter("all"); }} className="btn btn-secondary btn-sm">Clear</button>
          <span className="toolbar-divider" />
          <OpenClosedFilterDropdown value={statusFilter} onChange={setStatusFilter} label="Status" />
        </div>
        <div className="toolbar">
          <button type="button" onClick={openNewSurvey} className="btn btn-primary btn-md">+ New Survey</button>
          {viewMode === "table" && (
            <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0} className="btn btn-danger btn-md" title={selectedVisibleCount === 0 ? "Select surveys to delete" : `Delete ${selectedVisibleCount} selected`}>
              Delete {selectedVisibleCount > 0 ? `(${selectedVisibleCount})` : ""}
            </button>
          )}
          <span className="toolbar-divider" />
          <ViewModeIconToggle
            value={viewMode}
            onChange={setViewMode}
            modes={["calendar", "table", "card"]}
            ariaLabel="View"
          />
          <span className="toolbar-divider" />
          <GlobalTimeNavigatorBar
            variant="inline"
            title={timeNav.title}
            view={timeNav.view}
            onViewChange={timeNav.setView}
            onToday={timeNav.goToday}
            onPrev={timeNav.movePrev}
            onNext={timeNav.moveNext}
          />
        </div>

        <div className="table-scroll">
          {loading ? (
            <p className="empty-message">Loading surveys…</p>
          ) : sorted.length === 0 ? (
            <p className="empty-message">No surveys match your filters.</p>
          ) : (
            <>
              {viewMode === "table" && (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <input type="checkbox" checked={visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id))} onChange={() => toggleSelectAll(visibleIds)} title="Select all" />
                        </th>
                        <th className="sortable" onClick={() => handleSort("name")}>Name</th>
                        <th className="sortable" onClick={() => handleSort("start_date")}>Start date</th>
                        <th className="sortable" onClick={() => handleSort("end_date")}>End date</th>
                        <th className="sortable" onClick={() => handleSort("status")}>Status</th>
                        <th className="sortable" onClick={() => handleSort("invited")}>Invited</th>
                        <th className="sortable" onClick={() => handleSort("completed")}>Completed</th>
                        <th className="sortable" onClick={() => handleSort("responseRate")}>Response Rate</th>
                        <th className="sortable" onClick={() => handleSort("daysElapsed")}>Days elapsed</th>
                        <th className="sortable" onClick={() => handleSort("daysLeft")}>Days left</th>
                        <TableActionColumnHeader align="left" />
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((s) => (
                        <tr key={s._id}>
                          <td><input type="checkbox" checked={selectedIds.includes(s._id)} onChange={() => toggleSelected(s._id)} /></td>
                          <td>
                            <button type="button" onClick={() => navigate(`/Surveys/${s._id}/details`)} className="btn-link" title="View survey details">{s.name}</button>
                          </td>
                          <td>{s.start_date ? formatDueDate(s.start_date) : "—"}</td>
                          <td>{s.end_date ? formatDueDate(s.end_date) : "—"}</td>
                          <td>
                            {isExpired(s) ? <span className="badge" style={{ background: "#fef2f2", color: "#dc2626" }}>Expired</span> : (s.status || "Open").toLowerCase() === "closed" ? <span className="badge" style={{ background: "#f3f4f6", color: "#374151" }}>Closed</span> : <span className="badge" style={{ background: "#f0fdf4", color: "#16a34a" }}>Open</span>}
                          </td>
                          <td style={{ textAlign: "right" }}>{s.invited ?? "—"}</td>
                          <td style={{ textAlign: "right" }}>{s.completed ?? "—"}</td>
                          <td style={{ textAlign: "right" }}>
                            {(s.invited || 0) > 0 ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                                <div style={{ width: 56, height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                                  <div style={{ width: `${Math.min(100, responseRatePct(s.invited, s.completed))}%`, height: "100%", background: "#22c55e", borderRadius: 3 }} />
                                </div>
                                <span>{formatResponseRate(s.invited, s.completed)}</span>
                              </div>
                            ) : "—"}
                          </td>
                          <td style={{ textAlign: "right" }}>{daysElapsed(s.start_date) ?? "—"}</td>
                          <td style={{ textAlign: "right" }}>{daysLeft(s.end_date) ?? "—"}</td>
                          <td>
                            <RowActionIcons>
                              <button type="button" onClick={() => navigate(`/Surveys/${s._id}/details?tab=results`)} className="btn-link" style={iconBtnStyle} title="View Report"><IconView size={18} /></button>
                              <button type="button" onClick={() => openEditSurvey(s)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                              <button type="button" onClick={() => openQuestions(s)} className="btn-link" style={iconBtnStyle} title="Questions"><IconQuestions size={18} /></button>
                              <button type="button" onClick={() => duplicateSurveyRow(s)} className="btn-link" style={iconBtnStyle} title="Duplicate"><IconDuplicate size={18} /></button>
                              {(s.status || "Open").toLowerCase() === "open" && (
                                <button type="button" disabled={closingId === s._id} onClick={() => handleCloseSurveyRow(s._id)} className={closingId === s._id ? "btn-link-muted" : "btn-link-danger"} style={closingId === s._id ? undefined : iconBtnStyle} title="Mark Complete">
                                  {closingId === s._id ? "Closing…" : <IconClose size={18} />}
                                </button>
                              )}
                              {(s.status || "").toLowerCase() === "closed" && (
                                <button type="button" disabled={reopeningId === s._id} onClick={() => handleReopenSurveyRow(s._id)} className={reopeningId === s._id ? "btn-link-muted" : "btn-link-success"} style={iconBtnStyle} title="Reopen">
                                  {reopeningId === s._id ? "Reopening…" : <IconReopen size={18} />}
                                </button>
                              )}
                            </RowActionIcons>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewMode === "card" && (
                <div className="cards-wrapper">
                  {sorted.map((s) => (
                    <article
                      key={s._id}
                      className="card"
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        padding: "14px",
                        backgroundColor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        minWidth: 0,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#495057", fontSize: "13px" }}>
                        <IconCalendar size={18} aria-hidden />
                        <span>
                          {s.start_date ? formatDueDate(s.start_date) : "—"}
                          {" → "}
                          {s.end_date ? formatDueDate(s.end_date) : "—"}
                        </span>
                      </div>
                      <h3 className="card-title" style={{ margin: 0, fontSize: "1.05rem" }}>
                        <button
                          type="button"
                          onClick={() => navigate(`/Surveys/${s._id}/details`)}
                          className="btn-link"
                          style={{ padding: 0, textAlign: "left", fontWeight: 600 }}
                          title="View survey details"
                        >
                          {s.name || "Survey"}
                        </button>
                      </h3>
                      <div>
                        {isExpired(s) ? (
                          <span className="badge" style={{ background: "#fef2f2", color: "#dc2626" }}>Expired</span>
                        ) : (s.status || "Open").toLowerCase() === "closed" ? (
                          <span className="badge" style={{ background: "#f3f4f6", color: "#374151" }}>Closed</span>
                        ) : (
                          <span className="badge" style={{ background: "#f0fdf4", color: "#16a34a" }}>Open</span>
                        )}
                      </div>
                      {(s.invited || 0) > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "13px" }}>
                          <span style={{ color: "#6c757d" }}>Response</span>
                          <div style={{ flex: 1, maxWidth: 120, height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(100, responseRatePct(s.invited, s.completed))}%`, height: "100%", background: "#22c55e", borderRadius: 3 }} />
                          </div>
                          <span>{formatResponseRate(s.invited, s.completed)}</span>
                        </div>
                      )}
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", flexWrap: "wrap", marginTop: "4px" }}>
                        <button type="button" onClick={() => navigate(`/Surveys/${s._id}/details?tab=results`)} className="btn-link" style={iconBtnStyle} title="View Report"><IconView size={18} /></button>
                        <button type="button" onClick={() => openEditSurvey(s)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                        <button type="button" onClick={() => openQuestions(s)} className="btn-link" style={iconBtnStyle} title="Questions"><IconQuestions size={18} /></button>
                        <button type="button" onClick={() => duplicateSurveyRow(s)} className="btn-link" style={iconBtnStyle} title="Duplicate"><IconDuplicate size={18} /></button>
                        {(s.status || "Open").toLowerCase() === "open" && (
                          <button type="button" disabled={closingId === s._id} onClick={() => handleCloseSurveyRow(s._id)} className={closingId === s._id ? "btn-link-muted" : "btn-link-danger"} style={closingId === s._id ? undefined : iconBtnStyle} title="Mark Complete">
                            {closingId === s._id ? "Closing…" : <IconClose size={18} />}
                          </button>
                        )}
                        {(s.status || "").toLowerCase() === "closed" && (
                          <button type="button" disabled={reopeningId === s._id} onClick={() => handleReopenSurveyRow(s._id)} className={reopeningId === s._id ? "btn-link-muted" : "btn-link-success"} style={iconBtnStyle} title="Reopen">
                            {reopeningId === s._id ? "Reopening…" : <IconReopen size={18} />}
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {viewMode === "calendar" && (
                <ScheduleCalendar
                  items={sorted}
                  view={timeNav.view}
                  focusDateYmd={timeNav.focusDateYmd}
                  onItemClick={(s) => navigate(`/Surveys/${s._id}/details`)}
                  emptyDayMessage="No surveys starting this day"
                  getDateValue={(s) => s?.start_date}
                  getLabel={surveyCalendarLabel}
                />
              )}
            </>
          )}
        </div>

      {questionSurvey && !(wizardContext && wizardContext.open) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "12px",
            boxSizing: "border-box",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeQuestions();
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="survey-questions-title"
        >
          <div
            className="panel"
            style={{
              maxWidth: "900px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              padding: "14px 18px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="survey-questions-title" className="panel-title" style={{ margin: "0 0 10px 0", fontSize: "1.1rem" }}>
              Questions – {questionSurvey.name}
            </h3>
            {wizardContext && wizardContext.surveyId === questionSurvey._id && (
              <div className="panel-hint" style={{ marginBottom: "6px" }}>
                Step 2 of 3 – define questions
              </div>
            )}
            {loadingQuestions ? (
              <p className="panel-hint">Loading questions…</p>
            ) : (
              <form onSubmit={saveQuestions}>
                <p className="panel-hint" style={{ marginBottom: "8px" }}>
                  Define the questions that recipients will see in the survey wizard. Reorder
                  questions using the Order field. For choice and Likert questions, add options
                  below and reorder them by dragging the handle on each option row.
                </p>
                <div className="toolbar" style={{ marginBottom: "8px" }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={addQuestion}>
                    + Add question
                  </button>
                </div>
                {questions.length === 0 && (
                  <p style={{ color: "#6c757d", fontSize: "13px" }}>
                    No questions defined yet. Add one above.
                  </p>
                )}
                {questions.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {questions.map((q, idx) => (
                      <div
                        key={idx}
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          padding: "5px 8px",
                          background: "#f8f9fa",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "4px",
                            gap: "6px",
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: "11px", color: "#495057" }}>
                            Question {idx + 1}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeQuestion(idx)}
                            style={{
                              padding: "2px 8px",
                              fontSize: "11px",
                              border: "1px solid #dc3545",
                              borderRadius: "4px",
                              background: "#fff",
                              color: "#dc3545",
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(200px, 2fr) minmax(160px, 1.2fr) minmax(80px, 0.6fr)",
                            gap: "4px 8px",
                            alignItems: "flex-start",
                          }}
                        >
                          <div>
                            <label
                              style={{
                                display: "block",
                                fontSize: "11px",
                                marginBottom: "1px",
                              }}
                            >
                              Question text
                            </label>
                            <textarea
                              rows={2}
                              value={q.question_text}
                              onChange={(e) =>
                                updateQuestion(idx, "question_text", e.target.value)
                              }
                              className="input"
                              style={{
                                width: "100%",
                                resize: "vertical",
                                minHeight: "38px",
                                fontSize: "12px",
                                lineHeight: 1.25,
                                padding: "3px 6px",
                              }}
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                fontSize: "11px",
                                marginBottom: "1px",
                              }}
                            >
                              Question type
                            </label>
                            <select
                              value={q.question_type}
                              onChange={(e) =>
                                updateQuestion(idx, "question_type", e.target.value)
                              }
                              className="input input-full"
                              style={{ fontSize: "12px", padding: "3px 6px", minHeight: "30px" }}
                            >
                              {QUESTION_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                fontSize: "11px",
                                marginBottom: "1px",
                              }}
                            >
                              Order
                            </label>
                            <input
                              type="text"
                              value={q.order_index}
                              onChange={(e) =>
                                updateQuestion(idx, "order_index", Number(e.target.value))
                              }
                              className="input input-full input-number-no-spin"
                              style={{ fontSize: "12px", padding: "3px 6px", minHeight: "30px" }}
                            />
                          </div>
                        </div>
                        {["single_choice", "multi_choice", "likert"].includes(
                          q.question_type
                        ) && (
                          <div style={{ marginTop: "4px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "11px",
                                marginBottom: "2px",
                                fontWeight: 600,
                                color: "#495057",
                              }}
                            >
                              Options
                            </label>
                            <SortableCrudOptionList
                              items={q.optionsList || []}
                              onChange={(next) =>
                                updateQuestion(idx, "optionsList", next)
                              }
                              addPlaceholder="Add new option…"
                              ariaLabel={`Options for question ${idx + 1}`}
                            />
                          </div>
                        )}
                        {(q.question_type === "rating" || q.question_type === "nps") && (
                          <div
                            style={{
                              marginTop: "4px",
                              display: "flex",
                              gap: "6px",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "12px",
                                  marginBottom: "2px",
                                }}
                              >
                                Min
                              </label>
                            <input
                              type="text"
                              value={q.scale_min}
                              onChange={(e) =>
                                  updateQuestion(idx, "scale_min", e.target.value)
                                }
                              className="input input-min-80 input-number-no-spin"
                            />
                            </div>
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "12px",
                                  marginBottom: "2px",
                                }}
                              >
                                Max
                              </label>
                              <input
                                type="text"
                                value={q.scale_max}
                                onChange={(e) =>
                                  updateQuestion(idx, "scale_max", e.target.value)
                                }
                                className="input input-min-80 input-number-no-spin"
                              />
                            </div>
                            <div style={{ fontSize: "11px", color: "#6c757d" }}>
                              Rating: use 1–5 or 1–10. NPS: 0–10.
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginTop: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeQuestions}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      background: "#fff",
                      color: "#495057",
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={savingQuestions}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      border: "none",
                      borderRadius: "4px",
                      background: "#0d6efd",
                      color: "#fff",
                      cursor: savingQuestions ? "not-allowed" : "pointer",
                    }}
                  >
                    {savingQuestions ? "Saving…" : "Save questions"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {wizardContext && wizardContext.step === 3 && wizardSummary && wizardSummary.survey && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "12px",
            boxSizing: "border-box",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setWizardSummary(null);
              setWizardContext(null);
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="survey-summary-title"
        >
          <div
            className="panel"
            style={{
              maxWidth: "800px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              padding: "14px 18px",
              fontSize: "13px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="survey-summary-title" className="panel-title" style={{ margin: "0 0 10px 0", fontSize: "1.1rem" }}>
              Survey summary
            </h3>
            <div style={{ marginBottom: "10px" }}>
              <div className="panel-title" style={{ fontWeight: 600 }}>{wizardSummary.survey.name}</div>
              <div className="panel-hint" style={{ marginTop: "2px" }}>
                {wizardSummary.survey.start_date
                  ? dayjs(wizardSummary.survey.start_date).format("MM/DD/YYYY")
                  : "—"}
                {" → "}
                {wizardSummary.survey.end_date
                  ? dayjs(wizardSummary.survey.end_date).format("MM/DD/YYYY")
                  : "—"}
                {" · "}
                Status: {wizardSummary.survey.status || "Open"}
              </div>
              {wizardSummary.survey.description && (
                <div style={{ marginTop: "4px" }}>{wizardSummary.survey.description}</div>
              )}
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                Questions ({wizardSummary.questions.length})
              </div>
              {wizardSummary.questions.length === 0 ? (
                <div style={{ color: "#dc3545" }}>
                  No questions defined. Recipients will see an empty survey.
                </div>
              ) : (
                <ol style={{ paddingLeft: "18px", margin: 0 }}>
                  {wizardSummary.questions.map((q, idx) => (
                    <li key={idx} style={{ marginBottom: "6px" }}>
                      <div>
                        <strong>{q.question_text || "(No text)"}</strong>
                      </div>
                      <div style={{ color: "#6c757d" }}>
                        Type: {q.question_type}
                        {Array.isArray(q.options) && q.options.length > 0 && (
                          <> · Options: {q.options.join(", ")}</>
                        )}
                        {q.scale_min != null &&
                          q.scale_max != null &&
                          q.question_type &&
                          (q.question_type === "rating" || q.question_type === "nps") && (
                            <> · Scale: {q.scale_min}–{q.scale_max}</>
                          )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setWizardSummary(null);
                  setWizardContext(null);
                }}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: "#fff",
                  color: "#495057",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {wizardContext && wizardContext.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
            padding: "12px",
            boxSizing: "border-box",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingSurvey(null);
              setWizardContext(null);
              setWizardSummary(null);
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="survey-wizard-title"
        >
          <div
            className="panel"
            style={{
              maxWidth: "900px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              padding: "14px 18px",
              fontSize: "13px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="survey-wizard-title" className="panel-title" style={{ margin: "0 0 10px 0", fontSize: "1.1rem" }}>
              {wizardContext.mode === "edit" ? "Edit survey" : "New survey"}
            </h3>
            <div className="panel-hint" style={{ marginBottom: "8px" }}>
              Step 1 of 3 – basic survey details
            </div>
            <form
              onSubmit={saveSurvey}
              style={{
                padding: "6px 0 0 0",
                fontSize: "13px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(200px, 1.5fr) repeat(3, minmax(120px, 1fr))",
                  gap: "8px 12px",
                  alignItems: "center",
                }}
              >
                <div>
                  <label style={{ display: "block", fontSize: "12px", marginBottom: "2px" }}>
                    Name<span style={{ color: "#dc3545" }}> *</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleFormChange("name", e.target.value)}
                    className="input input-full"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", marginBottom: "2px" }}>
                    Start date<span style={{ color: "#dc3545" }}> *</span>
                  </label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => handleFormChange("start_date", e.target.value)}
                    className="input input-full"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", marginBottom: "2px" }}>
                    End date<span style={{ color: "#dc3545" }}> *</span>
                  </label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => handleFormChange("end_date", e.target.value)}
                    className="input input-full"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", marginBottom: "2px" }}>
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => handleFormChange("status", e.target.value)}
                    className="input input-full"
                  >
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: "8px" }}>
                <label style={{ display: "block", fontSize: "12px", marginBottom: "2px" }}>
                  Description
                </label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => handleFormChange("description", e.target.value)}
                  className="input" style={{ width: "100%", resize: "vertical" }}
                />
              </div>
              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setEditingSurvey(null);
                    setWizardContext(null);
                    setWizardSummary(null);
                  }}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    background: "#fff",
                    color: "#495057",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={savingSurvey}
                  onClick={() => saveSurvey(null, { skipQuestions: true })}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    border: "1px solid #0d6efd",
                    borderRadius: "4px",
                    background: "#fff",
                    color: "#0d6efd",
                    cursor: savingSurvey ? "not-allowed" : "pointer",
                    minWidth: "110px",
                  }}
                >
                  {savingSurvey ? "Saving…" : "Save & Close"}
                </button>
                <button
                  type="submit"
                  disabled={savingSurvey}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    border: "none",
                    borderRadius: "4px",
                    background: "#0d6efd",
                    color: "#fff",
                    cursor: savingSurvey ? "not-allowed" : "pointer",
                    minWidth: "100px",
                  }}
                >
                  {savingSurvey ? "Saving…" : "Next: Questions"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Surveys;

